'use client';

import React, { useState } from 'react';

import {
    Alignable,
    Box,
    Column,
    Columns,
    Divider,
    Icon,
    IconName,
    IconSize,
    Price,
    PriceLevel,
    Row,
    Text,
    TextLevels,
    TypographyBold,
    View,
} from '@bytel/trilogy-react';

import { WallType } from '@app-types/config';
import { DiscountEnumType } from '@app-types/discount';
import { FundingType } from '@app-types/funding';
import { MobilePlanType } from '@app-types/plan';
import { Product } from '@app-types/product';

import { ImageThumbor } from '@components/image-thumbor';
import { InlinePrice } from '@components/inline-price';
import { ProductDetailsModal } from '@components/product-details-modal';

type PhoneInCartProps = {
    phone?: Product;
    selectedFunding?: FundingType;
    isFloating?: boolean;
    plan: MobilePlanType;
};

export function PhoneInCart({ phone, isFloating, plan, selectedFunding }: PhoneInCartProps) {
    const [isPriceDetailModalOpen, setIsPriceDetailModalOpen] = useState(false);
    if (!phone || !selectedFunding) {
        return null;
    }

    const phoneAmount =
        selectedFunding.initialDeposit -
        (phone?.details?.discounts?.find((discount) => discount.type === DiscountEnumType.ODR)?.discount || 0);
    const hasOdr = phone.details?.discounts?.find((discount) => discount.type === DiscountEnumType.ODR);

    return (
        <>
            <Row narrow>
                {isFloating ? (
                    <Columns align={Alignable.ALIGNED_CENTER} verticalAlign={Alignable.ALIGNED_CENTER} mobile>
                        <Column>
                            <Divider />
                        </Column>
                        <Column narrow>
                            <PhoneInCartBox
                                phone={phone}
                                selectedFunding={selectedFunding}
                                phoneAmount={phoneAmount}
                                setIsPriceDetailModalOpen={setIsPriceDetailModalOpen}
                                hasOdr={!!hasOdr}
                            />
                        </Column>
                        <Column>
                            <Divider />
                        </Column>
                    </Columns>
                ) : (
                    <PhoneInCartBox
                        phone={phone}
                        selectedFunding={selectedFunding}
                        phoneAmount={phoneAmount}
                        setIsPriceDetailModalOpen={setIsPriceDetailModalOpen}
                        hasOdr={!!hasOdr}
                    />
                )}
            </Row>
            <Row narrow>
                <View flexable fullwidth className='is-centered'>
                    <Icon size={IconSize.SMALL} name={IconName.PLUS_CIRCLE} />
                </View>
                <ProductDetailsModal
                    plan={plan}
                    currentProduct={phone}
                    onClose={() => setIsPriceDetailModalOpen(false)}
                    type={WallType.PHONE}
                    selectedFunding={selectedFunding}
                    modalOpen={!!phone && isPriceDetailModalOpen}
                />
            </Row>
        </>
    );
}

type PhoneInCartBoxProps = {
    phone?: Product;
    selectedFunding?: FundingType;
    phoneAmount: number;
    setIsPriceDetailModalOpen: (open: boolean) => void;
    hasOdr: boolean;
};

export function PhoneInCartBox({
    phone,
    phoneAmount,
    selectedFunding,
    setIsPriceDetailModalOpen,
    hasOdr,
}: PhoneInCartBoxProps) {
    return (
        <Box>
            <Columns mobile verticalAlign={Alignable.ALIGNED_CENTER}>
                <Column narrow>
                    {phone?.image && <ImageThumbor src={`${phone.image}`} alt='' width={55} height={110} />}
                </Column>
                <Column className='has-text-centered-mobile'>
                    <Text typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD} level={TextLevels.TWO}>
                        {phone?.brand} {phone?.name}
                    </Text>
                    {!!phoneAmount && (
                        <Price
                            amount={Math.max(phoneAmount, 1)}
                            level={PriceLevel.SIX}
                            className='is-fullwidth is-justified-center-mobile'
                        />
                    )}
                    <Text level={TextLevels.THREE} marginless className='min-height'>
                        {!!selectedFunding?.monthlyAmount && (
                            <>
                                <span>+</span>
                                <InlinePrice
                                    level={TextLevels.THREE}
                                    amount={selectedFunding?.monthlyAmount}
                                    period='mois'
                                    typo={[]}
                                />
                                <span>&nbsp;x {selectedFunding.installmentCount} mois&nbsp;</span>
                                {!hasOdr && (
                                    <Icon
                                        onClick={() => setIsPriceDetailModalOpen(true)}
                                        name={IconName.INFOS_CIRCLE}
                                        size={IconSize.SMALLER}
                                    />
                                )}
                            </>
                        )}
                    </Text>

                    {hasOdr && (
                        <Text marginless level={TextLevels.THREE}>
                            après remboursement&nbsp;
                            <Icon
                                onClick={() => setIsPriceDetailModalOpen(true)}
                                name={IconName.INFOS_CIRCLE}
                                size={IconSize.SMALLER}
                            />
                        </Text>
                    )}
                </Column>
            </Columns>
        </Box>
    );
}
